/**
 * Which language this session is rendered in.
 *
 * The default comes from the installation, not from the browser: the DR Congo
 * installation serves a French-speaking market and opens in French, the
 * Tanzania installation in English. An operator can still switch, because
 * bank staff in either country may prefer the other language, and the choice
 * is remembered per browser rather than per user account.
 */
import { getInstallation } from '../installation';
import dictionary from './fr';

export type Language = 'en' | 'fr';

const STORAGE_KEY = 'gs-language';

let current: Language | null = null;

export const getLanguage = (): Language => {
    if (current) return current;
    const stored = localStorage?.getItem(STORAGE_KEY);
    current = stored === 'en' || stored === 'fr' ? stored : getInstallation().locale;
    return current;
};

/**
 * Switching language reloads the page.
 *
 * Translation happens as elements are created, so strings already rendered
 * are not re-evaluated when the dictionary changes. A reload is honest about
 * that rather than leaving half the screen in the previous language.
 */
export const setLanguage = (language: Language): void => {
    localStorage?.setItem(STORAGE_KEY, language);
    current = language;
    window.location.reload();
};

export const isFrench = (): boolean => getLanguage() === 'fr';

/**
 * Explicit lookup for sentences the automatic layer cannot reach.
 *
 * The layer matches whole strings, so prose that interpolates a value —
 * "a record from {country} is not present here" — never matches and stays
 * English. Those are exactly the explanatory paragraphs that carry an
 * argument rather than label a field, so on the screens that make a case to
 * the reader they are worth converting by hand.
 *
 * Placeholders are `{name}`. An unknown key returns the English it was given,
 * so a missing translation degrades to English rather than to a blank screen
 * or a raw key on a projector.
 */
export const t = (english: string, vars?: Record<string, string>): string => {
    const translated = isFrench() ? dictionary[english] || english : english;
    if (!vars) return translated;
    return Object.keys(vars).reduce((text, key) => text.split(`{${key}}`).join(vars[key]), translated);
};
